import React, { useState } from 'react';
import { Modal, Button, Form, ProgressBar, Nav } from 'react-bootstrap';
import { ANALYSIS_TYPES, ANALYSIS_TYPE_LABELS } from '../../constants/analysisTypes';
import { ProcessedBankStatement } from '../../constants/constants';
import BankStatementGapInputs from '../../components/analysis/BankStatementGapInputs';
import GLBankReconciliationInputs, { GLBankReconciliationInputData } from '../../components/analysis/GLBankReconciliationInputs';

interface CreateAnalysisModalProps {
  show: boolean;
  onHide: () => void;
  onSubmit: (analysisData: any) => Promise<void>;
  case_id: string;
}

const STEPS = [
  { key: 'details', title: 'Analysis Details' },
  { key: 'inputs', title: 'Analysis Inputs' },
  { key: 'confirm', title: 'Confirm & Start' }
];

type AnalysisType = typeof ANALYSIS_TYPES[keyof typeof ANALYSIS_TYPES];

const CreateAnalysisModal: React.FC<CreateAnalysisModalProps> = ({ show, onHide, onSubmit, case_id }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [validated, setValidated] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [analysisData, setAnalysisData] = useState<{
    type: AnalysisType;
    name: string;
    description: string;
    inputs: any;
  }>({
    type: ANALYSIS_TYPES.BANK_STATEMENT_GAP,
    name: '',
    description: '',
    inputs: {
      dateRange: { from: '', to: '' },
      includedStatements: [] as ProcessedBankStatement[]
    }
  });

  const isStepValid = () => {
    switch (currentStep) {
      case 0:
        return true; // No required fields in first step
      case 1:
        if (analysisData.type === ANALYSIS_TYPES.BANK_STATEMENT_GAP) {
          return analysisData.inputs.dateRange?.from &&
                 analysisData.inputs.dateRange?.to &&
                 analysisData.inputs.includedStatements?.length > 0;
        }
        if (analysisData.type === ANALYSIS_TYPES.CASH_ACTIVITY_REPORT) {
          return analysisData.inputs.dateRange?.from &&
                 analysisData.inputs.dateRange?.to &&
                 analysisData.inputs.includedStatements?.length > 0;
        }
        if (analysisData.type === ANALYSIS_TYPES.GL_BANK_RECONCILIATION) {
          return !!analysisData.inputs.gl_file_id &&
                 !!analysisData.inputs.gl_account_name &&
                 analysisData.inputs.statement_ids?.length > 0 &&
                 !!analysisData.inputs.date_range?.from &&
                 !!analysisData.inputs.date_range?.to;
        }
        return true;
      case 2:
        return true;
      default:
        return false;
    }
  };

  const handleNext = () => {
    setValidated(true); // Always show validation state when Next is clicked
    
    if (isStepValid()) {
      if (currentStep < STEPS.length - 1) {
        setCurrentStep(currentStep + 1);
        setValidated(false);
      } else {
        handleSubmit();
      }
    }
    // If not valid, the validation feedback will show but we won't advance
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      setValidated(false);
    }
  };

  const handleSubmit = async () => {
    let submitData = analysisData;
    // Transform BankStatementGapInputs shape → Lambda-expected shape for CASH_ACTIVITY_REPORT
    if (analysisData.type === ANALYSIS_TYPES.CASH_ACTIVITY_REPORT) {
      submitData = {
        ...analysisData,
        inputs: {
          statement_ids: (analysisData.inputs.includedStatements ?? []).map((s: any) => s.statement_id),
          date_range: analysisData.inputs.dateRange ?? { from: '', to: '' },
        },
      };
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      await onSubmit(submitData);
      onHide();
    } catch (e: any) {
      // Keep the modal open (and the user's inputs) so the failure is visible
      // and they can retry, rather than silently closing on a failed request.
      setSubmitError(e?.response?.data?.error ?? e?.message ?? 'Failed to start analysis');
    } finally {
      setSubmitting(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <Form noValidate validated={validated}>
            <Form.Group className="mb-3">
              <Form.Label>Analysis Type</Form.Label>
              <Form.Select
                value={analysisData.type}
                onChange={(e) => setAnalysisData({
                  ...analysisData,
                  type: e.target.value as AnalysisType
                })}
              >
                {Object.entries(ANALYSIS_TYPE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Analysis Name</Form.Label>
              <Form.Control
                type="text"
                value={analysisData.name}
                onChange={(e) => setAnalysisData({
                  ...analysisData,
                  name: e.target.value
                })}
                placeholder="Enter a name for this analysis"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description (Optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={analysisData.description}
                onChange={(e) => setAnalysisData({
                  ...analysisData,
                  description: e.target.value
                })}
                placeholder="Enter a description"
              />
            </Form.Group>
          </Form>
        );
      case 1:
        if (analysisData.type === ANALYSIS_TYPES.BANK_STATEMENT_GAP) {
          return (
            <Form noValidate validated={validated}>
              <BankStatementGapInputs
                case_id={case_id}
                onChange={(inputData) => setAnalysisData({ ...analysisData, inputs: inputData })}
                validated={validated}
              />
              {validated && !isStepValid() && (
                <div className="text-danger mt-2">
                  Please select a date range and at least one statement
                </div>
              )}
            </Form>
          );
        }
        if (analysisData.type === ANALYSIS_TYPES.CASH_ACTIVITY_REPORT) {
          return (
            <Form noValidate validated={validated}>
              <BankStatementGapInputs
                case_id={case_id}
                onChange={(inputData) => setAnalysisData({ ...analysisData, inputs: inputData })}
                validated={validated}
              />
              {validated && !isStepValid() && (
                <div className="text-danger mt-2">
                  Please select a date range and at least one statement
                </div>
              )}
            </Form>
          );
        }
        if (analysisData.type === ANALYSIS_TYPES.GL_BANK_RECONCILIATION) {
          return (
            <Form noValidate validated={validated}>
              <GLBankReconciliationInputs
                case_id={case_id}
                onChange={(inputData: GLBankReconciliationInputData) =>
                  setAnalysisData({ ...analysisData, inputs: inputData })
                }
                validated={validated}
              />
              {validated && !isStepValid() && (
                <div className="text-danger mt-2">
                  Please select a GL file, account, date range, and at least one statement
                </div>
              )}
            </Form>
          );
        }
        return <div className="text-muted">No inputs required for this analysis type.</div>;
      case 2:
        return (
          <div>
            <h5>Analysis Summary</h5>
            <dl className="row">
              <dt className="col-sm-3">Type</dt>
              <dd className="col-sm-9">{analysisData.type}</dd>
              
              <dt className="col-sm-3">Name</dt>
              <dd className="col-sm-9">{analysisData.name}</dd>
              
              {analysisData.description && (
                <>
                  <dt className="col-sm-3">Description</dt>
                  <dd className="col-sm-9">{analysisData.description}</dd>
                </>
              )}
            </dl>
            <div className="alert alert-info">
              Are you ready to start this analysis?
            </div>
          </div>
        );
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Create New Analysis</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Nav className="nav-pills nav-fill mb-3">
          {STEPS.map((step, index) => (
            <Nav.Item key={step.key}>
              <Nav.Link
                active={currentStep === index}
                onClick={() => setCurrentStep(index)}
                disabled={index > currentStep}
              >
                {index + 1}. {step.title}
              </Nav.Link>
            </Nav.Item>
          ))}
        </Nav>
        <ProgressBar 
          now={(currentStep + 1) * (100 / STEPS.length)} 
          className="mb-4"
        />
        {renderStepContent()}
        {submitError && (
          <div className="alert alert-danger mt-3 mb-0">
            Couldn't start this analysis: {submitError}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={submitting}>
          Cancel
        </Button>
        {currentStep > 0 && (
          <Button variant="outline-primary" onClick={handleBack} disabled={submitting}>
            Back
          </Button>
        )}
        <Button
          variant="primary"
          onClick={handleNext}
          disabled={submitting}
        >
          {submitting ? 'Starting…' : currentStep === STEPS.length - 1 ? 'Start Analysis' : 'Next'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateAnalysisModal; 